import React, { useMemo, useState } from 'react';
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import {
  Checkbox,
  Button,
  Typography,
  Stack,
  Chip,
  Link,
  TextField,
} from '@mui/material';
import { ViewInAr as CubeIcon } from '@mui/icons-material';
import { Columns } from '@/types';
import { TableData } from '@/types/table';
import { abyssColors, primary } from '@/theme/colors';
import { getColumnDimensions } from './utils';
import { useEquipmentData } from '@/hooks/useEquipmentData';
import { titleCaseSentence, formatEnumForDisplay } from '@/utils';
import { PoiIconsList } from '@/components/Analysis/shared/EquipmentTable/PoiIconsList';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  editableCellTdStyle,
  inlineEditInputSx,
  nonEditableCellTdStyle,
} from './styles';
import { CustomDropDown } from './CustomDropDown';
import { LazyDatePickerCell } from './LazyDatePickerCell';
import { BoundedTooltip } from './BoundedTooltip';
import { ReportsCell } from '@/components/shared/ComponentReportsTable';

interface PoiCount {
  poiType: string;
  count: number;
}

interface CustomCellProps extends Omit<GridCustomCellProps, 'isSelected'> {
  tooltipContainer?: HTMLElement | null;
  column?: Columns;
  totalColumns?: number;
  isFirstColumn?: boolean;
  isSelected?: boolean;
  isChecked?: boolean;
  onCheckboxChange?: () => void;
  onCellClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: TableData) => void;
  saveSingleEdit?: (itemId: string, columnKey: string, value: string) => Promise<void>;
  setEdit?: React.Dispatch<React.SetStateAction<EditDescriptor>>;
  canEdit?: boolean;
  onSave?: (rowId: string, fieldKey: string, value: unknown) => Promise<void>;
  exitEdit?: () => void;
  columns?: Columns[];
  isEditMode?: boolean;
}

const GOVERNING_BLISTER_VIEWPOINT_LABEL = 'GOVERNING BLISTER VIEWPOINT';

export const CustomCell: React.FC<CustomCellProps> = ({
  tdProps,
  dataItem,
  tooltipContainer,
  column: columnProp,
  field: fieldKey,
  totalColumns = 0,
  isFirstColumn,
  isSelected,
  isChecked,
  onCheckboxChange,
  onCellClick,
  saveSingleEdit,
  setEdit,
  canEdit = true,
  // Edit mode props
  onSave,
  exitEdit,
  columns = [],
  isEditMode = false,
}) => {
  const { showSnackBar } = useSnackBarMessage({});
  const { getCellValue } = useEquipmentData();
  const structureLocations = useAtomValue(state.structureLocations);
  const column = useMemo(() => {
    if (isEditMode && fieldKey) {
      return columns.find((col) => col.key === fieldKey) || columnProp!;
    }
    return columnProp!;
  }, [isEditMode, fieldKey, columns, columnProp]);

  const { value: displayValue } = getCellValue(dataItem, column);
  const [value, setValue] = useState<string>(String(displayValue));
  const isPlanner = column?.isPlannerColumn || String(column?.key).startsWith('planner');
  const foundViewpoint = useMemo(() => {
    if (column?.columnName !== GOVERNING_BLISTER_VIEWPOINT_LABEL) {
      return undefined;
    }
    return structureLocations?.find((location) => location.id === displayValue)?.name ?? 'N/A';
  }, [column?.columnName, structureLocations, displayValue]);


  if (isEditMode && exitEdit && onSave && column) {
    const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();
    const cellType = column?.displayConfig?.type;
    const isEditable = column?.enableInlineEdit && canEdit;

    const handleSaveValue = async (newValue: unknown) => {
      if (!dataItem.id || !fieldKey) return exitEdit();
      if (displayValue === newValue) {
        exitEdit();
        return;
      }

      try {
        await onSave(dataItem.id, fieldKey, newValue);
        showSnackBar('Cell value edited successfully', 'success');
      } catch (err) {
        console.error('Failed to save inline edit', err);
      }
      exitEdit();
    };

    if (!isEditable) {
      return (
        <td {...tdProps} style={nonEditableCellTdStyle}>
          {value || 'N/A'}
        </td>
      );
    }

    const onEnter = (val: string) => (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') return exitEdit();
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveValue(val);
      }
    };

    const commonTextFieldProps = {
      autoFocus: true,
      fullWidth: true,
      variant: 'outlined' as const,
      size: 'small' as const,
      onClick: stopPropagation,
      onMouseDown: stopPropagation,
      sx: inlineEditInputSx,
    };

    switch (cellType) {
      case 'NUMBER':
        return (
          <td {...tdProps} style={editableCellTdStyle}>
            <TextField
              {...commonTextFieldProps}
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (
                  !/[\d.-]/.test(e.key) &&
                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)
                ) {
                  e.preventDefault();
                }
                if (e.key === 'Escape') return exitEdit();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveValue(Number(value));
                }
              }}
              inputProps={{
                inputMode: 'numeric',
              }}
            />
          </td>
        );

      default:
        return (
          <td {...tdProps} style={editableCellTdStyle}>
            <TextField
              {...commonTextFieldProps}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onEnter(value)}
              onBlur={() => handleSaveValue(value)}
            />
          </td>
        );
    }
  }

  const isView3D = column.key === 'view3D';
  const isStickyLeft = !!column.locked && !isView3D;
  const dimensions = getColumnDimensions(column, totalColumns);

  const baseCellStyle: React.CSSProperties = {
    backgroundColor: isSelected ? 'rgba(236, 239, 243, 255)' : isPlanner ? abyssColors.primary[50] : 'white',
    borderTop: '1px solid rgba(224, 224, 224, 224)',
    borderBottom: '1px solid rgba(224, 224, 224, 224)',
    borderLeft: '1px solid transparent',
    borderRight: '1px solid transparent',
    whiteSpace: 'nowrap',
    padding: dimensions.padding,
    fontSize: '1.4rem',
    color: abyssColors.primary[500],
    boxSizing: 'border-box',
  };

  const cellStyle: React.CSSProperties = isView3D
    ? {
      ...baseCellStyle,
      position: 'sticky',
      right: 0,
      zIndex: 2,
      background: isSelected ? 'rgba(236, 239, 243, 255)' : '#fff',
      boxShadow: '-4px 0px 4px 0px rgba(0, 0, 0, 0.05)',
    }
    : isStickyLeft
      ? {
        ...baseCellStyle,
        position: 'sticky',
        zIndex: 2,
      }
      : baseCellStyle;

  const cellClassName = isView3D
    ? 'sticky-right-cell equipment-data-cell'
    : isStickyLeft
      ? 'sticky-left-cell equipment-data-cell'
      : 'equipment-data-cell';

  const cellType = column.displayConfig?.type;
  const cellOptions: string[] = column.options || [];
  const isEditable = column.enableInlineEdit && canEdit;
  const leftFromColumn = (column as any).leftOffset as number | undefined;
  const baseTdProps = {
    ...tdProps,
    style: {
      ...(tdProps?.style || {}),
      ...cellStyle,
      ...(isStickyLeft && leftFromColumn !== undefined ? { left: leftFromColumn } : {}),
    },
  };

  const handleCellClick = (e: React.MouseEvent) => {
    if (!isEditable) return;

    e.stopPropagation();
    if (setEdit && column.key && dataItem.id) {
      setEdit({ [dataItem.id]: [String(column.key)] });
    }
  };

  if (isView3D || cellType === '3DBUTTON') {
    return (
      <td {...baseTdProps} className={cellClassName}>
        <BoundedTooltip
          boundaryElement={tooltipContainer}
          title="Ctrl + Click to open in new tab"
          placement="top"
          arrow
        >
          <Button
            sx={{ p: { sm: '4px', xl: '9px' }, height: 'min-content' }}
            startIcon={
              <CubeIcon
                sx={{
                  color: dataItem.isUncontextualized
                    ? abyssColors.primary[300]
                    : abyssColors.secondary[600],
                  fontSize: '2rem !important',
                }}
              />
            }
            onClick={(e) => onCellClick?.(e, dataItem)}
            disabled={dataItem.isUncontextualized}
          >
            3D
          </Button>
        </BoundedTooltip>
      </td>
    );
  }

  if (cellType === 'CHIP') {
    const iconColor = column.displayConfig?.color?.find((colorItem) => colorItem?.key === displayValue)?.value;
    return (
      <td {...baseTdProps} className={cellClassName}>
        <Chip
          sx={{
            backgroundColor: iconColor ?? abyssColors.neutral[400],
            height: { sm: '24px', xl: '32px' },
          }}
          label={titleCaseSentence(String(displayValue))}
        />
      </td>
    );
  }

  if (cellType === 'BULLET') {
    const iconColor = column.displayConfig?.color?.find((colorItem) => colorItem?.key === displayValue)?.value;
    return (
      <td {...baseTdProps} className={cellClassName}>
        <Stack direction="row" alignItems="baseline">
          <span
            style={{
              borderRadius: '50%',
              width: 8,
              height: 8,
              backgroundColor: iconColor ?? abyssColors.neutral[400],
              marginRight: '8px',
            }}
          />
          {displayValue}
        </Stack>
      </td>
    );
  }

  if (cellType === 'LINK') {
    return (
      <td {...baseTdProps} className={cellClassName}>
        {!displayValue || displayValue === 'N/A' ? (
          displayValue
        ) : (
          <Link href={`${window.location.origin}${displayValue}`} target="_blank" rel="noreferrer">
            Link
          </Link>
        )}
      </td>
    );
  }

  if (column.columnName === GOVERNING_BLISTER_VIEWPOINT_LABEL) {
    return (
      <td {...baseTdProps} className={cellClassName}>
        {foundViewpoint}
      </td>
    );
  }

  if (cellType === 'REPORTS') {
    return (
      <td {...baseTdProps} className={cellClassName} onClick={(e) => e.stopPropagation()}>
        <ReportsCell assemblyId={dataItem.id} tagName={dataItem.tagName} />
      </td>
    );
  }

  if (cellType === 'POI_ICONS') {
    let poiCounts = [];
    try {
      poiCounts = typeof displayValue === 'string' ? JSON.parse(displayValue) : Array.isArray(displayValue) ? displayValue : [];
    } catch (e) {
      poiCounts = [];
    }

    if (!poiCounts || poiCounts.length === 0) {
      return (
        <td {...baseTdProps} className={cellClassName}>
          <Typography fontSize="1.4rem" fontWeight={400} color={abyssColors.primary[300]}>
            {NOT_APPLICABLE_LABEL}
          </Typography>
        </td>
      );
    }

    const displayCount = poiCounts.reduce((sum: number, poi: PoiCount) => sum + (poi.count || 0), 0);
    const tooltipText = poiCounts
      .map((poi: PoiCount) => `${formatEnumForDisplay(poi.poiType)} (${poi.count})`)
      .join(' | ');

    return (
      <BoundedTooltip boundaryElement={tooltipContainer} title={tooltipText} arrow>
        <td {...baseTdProps} className={cellClassName}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            gap={3}
            sx={{ maxWidth: '100%', overflow: 'hidden' }}
          >
            <Typography
              fontSize="1.4rem"
              fontWeight={400}
              color={abyssColors.primary[500]}
              sx={{ flexShrink: 0 }}
            >
              {displayCount}
            </Typography>
            <PoiIconsList poiItems={poiCounts} size={2.2} spacing={-0.8} />
          </Stack>
        </td>
      </BoundedTooltip>
    );
  }

  if (cellType === 'DROPDOWN') {
    // If user doesn't have permission, show as plain text
    if (!isEditable) {
      return (
        <td {...baseTdProps} className={cellClassName}>
          <Typography fontSize="1.4rem" fontWeight={400} color={abyssColors.primary[500]}>
            {displayValue ? titleCaseSentence(String(displayValue)) : 'N/A'}
          </Typography>
        </td>
      );
    }

    return (
      <td
        {...baseTdProps}
        className={cellClassName}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <CustomDropDown
          value={displayValue as string}
          options={cellOptions}
          itemId={String(dataItem.id)}
          columnKey={String(column.key)}
          onSave={(itemId, columnKey, newValue) => {
            if (!saveSingleEdit) return;
            saveSingleEdit(itemId, columnKey, String(newValue))
              .then(() => showSnackBar('Cell value edited successfully', 'success'))
              .catch((err) => console.error('Failed to save dropdown inline edit', err));
          }}
        />
      </td>
    );
  }

  if (cellType === 'DATE') {
    // If user doesn't have permission, show as plain text without calendar icon
    if (!isEditable) {
      const dateDisplayValue = displayValue ? String(displayValue).slice(0, 10) : 'N/A';
      return (
        <td {...baseTdProps} className={cellClassName}>
          <Typography fontSize="1.4rem" fontWeight={400} color={abyssColors.primary[500]}>
            {dateDisplayValue}
          </Typography>
        </td>
      );
    }

    return (
      <td
        {...baseTdProps}
        className={cellClassName}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <LazyDatePickerCell
          value={displayValue as string}
          onSave={(newValue) => {
            if (!saveSingleEdit) return;
            saveSingleEdit(String(dataItem.id), String(column.key), String(newValue ?? ''))
              .then(() => showSnackBar('Cell value edited successfully', 'success'))
              .catch((err) => console.error('Failed to save date inline edit', err));
          }}
          nativePickerAnchor="right"
          nativePickerInset={{ right: 50 }}
          placeholder={column.columnName ?? 'Add Date'}
        />
      </td>
    );
  }

  return (
    <td
      {...baseTdProps}
      className={`${cellClassName} ${isSelected ? 'selected' : ''}`}
      onClick={handleCellClick}
      style={{
        ...baseTdProps?.style,
        ...cellStyle,
        cursor: isEditable ? 'pointer' : undefined,
      }}
    >
      {isFirstColumn && (
        <Checkbox
          onChange={(event) => {
            event.stopPropagation();
            onCheckboxChange?.();
          }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          checked={isChecked}
          sx={{
            color: primary.light,
            p: { sm: '4px', xl: '9px' },
          }}
          disableRipple
        />
      )}
      {displayValue || 'N/A'}
    </td>
  );
};


